import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Grid, 
  TextField, 
  Button, 
  TableContainer, 
  Table, 
  TableHead, 
  TableBody, 
  TableRow, 
  TableCell, 
  IconButton, 
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Chip,
  Snackbar,
  Alert,
  TablePagination,
  FormHelperText,
  Divider
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { themeColors } from '../common/constants';
import api from '../../services/api';

// Office data for dropdown
const offices = [
  { label: 'Office of the Regional Director', value: 'Office of the Regional Director' },
  { label: 'Office of the Assistant Regional Director', value: 'Office of the Assistant Regional Director' },
  { label: 'Information Communication and Technology Unit', value: 'Information Communication and Technology Unit' },
  { label: 'Legal Unit', value: 'Legal Unit' },
  { label: 'Public Affairs Unit', value: 'Public Affairs Unit' },
  { label: 'Administrative Division', value: 'Administrative Division' },
  { label: 'Asset Management Section', value: 'Asset Management Section' },
  { label: 'Cash Section', value: 'Cash Section' },
  { label: 'General Services Unit', value: 'General Services Unit' },
  { label: 'Personnel Section', value: 'Personnel Section' },
  { label: 'Procurement Unit', value: 'Procurement Unit' },
  { label: 'Records Section', value: 'Records Section' },
  { label: 'Payroll Services Unit', value: 'Payroll Services Unit' },
  { label: 'Policy Planning and Research Division', value: 'Policy Planning and Research Division' },
  { label: 'Field Technical Assistance Division', value: 'Field Technical Assistance Division' },
  { label: 'Quality Assurance Division', value: 'Quality Assurance Division' },
  { label: 'Curriculum and Learning Management Division', value: 'Curriculum and Learning Management Division' },
  { label: 'Human Resource and Development Division', value: 'Human Resource and Development Division' },
  { label: 'NEAP - National Educators\' Academy of the Philippines', value: 'NEAP - National Educators\' Academy of the Philippines' },
  { label: 'Education Support Services Division', value: 'Education Support Services Division' },
  { label: 'Finance Division', value: 'Finance Division' }
];

const UserManagementTab = () => {
  // State for users data
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // State for pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // State for user dialog (add/edit)
  const [userDialog, setUserDialog] = useState({
    open: false,
    title: 'Add User',
    mode: 'add', // 'add' or 'edit'
    data: {
      id: null,
      name: '',
      email: '',
      password: '',
      password_confirmation: '',
      role: 'Staff',
      office: '',
      status: 'Active'
    },
    errors: {}
  });
  
  // State for delete confirmation dialog
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    userId: null,
    userName: ''
  });
  
  // State for view user dialog
  const [viewDialog, setViewDialog] = useState({
    open: false,
    user: null
  });
  
  // State for snackbar feedback
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  // Filter users based on search term
  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.office.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Fetch users from API
  const fetchUsers = () => {
    setLoading(true);
    api.get('/users')
      .then(response => {
        if (response.data.success) {
          setUsers(response.data.data);
        } else {
          showSnackbar('Failed to load users', 'error');
        }
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching users:', error);
        showSnackbar('Error loading users: ' + (error.response?.data?.message || error.message), 'error');
        setLoading(false);
      });
  };

  // Handle opening add user dialog
  const handleAddUser = () => {
    setUserDialog({
      open: true,
      title: 'Add User',
      mode: 'add',
      data: {
        id: null,
        name: '',
        email: '',
        password: '',
        password_confirmation: '',
        role: 'Staff',
        office: '',
        status: 'Active'
      },
      errors: {}
    });
  };

  // Handle opening edit user dialog
  const handleEditUser = (user) => {
    setUserDialog({
      open: true,
      title: 'Edit User',
      mode: 'edit',
      data: {
        id: user.id,
        name: user.name,
        email: user.email,
        password: '',
        password_confirmation: '',
        role: user.role,
        office: user.office,
        status: user.status
      },
      errors: {}
    });
  };

  // Handle view user details
  const handleViewUser = (user) => {
    setViewDialog({
      open: true,
      user
    });
  };

  // Handle opening delete confirmation dialog
  const handleDeleteConfirm = (user) => {
    setDeleteDialog({
      open: true,
      userId: user.id,
      userName: user.name
    });
  };

  // Handle input change in user dialog
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setUserDialog(prev => ({
      ...prev,
      data: {
        ...prev.data,
        [name]: value
      },
      errors: {
        ...prev.errors,
        [name]: undefined // Clear error on field change
      }
    }));
  };

  // Handle user dialog close
  const handleDialogClose = () => {
    setUserDialog(prev => ({
      ...prev,
      open: false
    }));
  };

  // Handle form submission for add/edit user
  const handleSubmitUser = () => {
    // Basic validation
    const errors = {};
    if (!userDialog.data.name) errors.name = 'Name is required';
    if (!userDialog.data.email) errors.email = 'Email is required';
    if (userDialog.mode === 'add' && !userDialog.data.password) errors.password = 'Password is required';
    if (userDialog.mode === 'add' && userDialog.data.password !== userDialog.data.password_confirmation) 
      errors.password_confirmation = 'Passwords do not match';
    if (!userDialog.data.role) errors.role = 'Role is required';
    if (!userDialog.data.office) errors.office = 'Office is required';
    if (!userDialog.data.status) errors.status = 'Status is required';
    
    if (Object.keys(errors).length > 0) {
      setUserDialog(prev => ({
        ...prev,
        errors
      }));
      return;
    }
    
    // Prepare data for API
    const userData = { ...userDialog.data };
    
    // If editing and password is empty, remove it from payload
    if (userDialog.mode === 'edit' && !userData.password) {
      delete userData.password;
      delete userData.password_confirmation;
    }
    
    // Determine if creating or updating a user
    const apiCall = userDialog.mode === 'add'
      ? api.post('/users', userData)
      : api.put(`/users/${userData.id}`, userData);
    
    apiCall
      .then(response => {
        if (response.data.success) {
          showSnackbar(
            userDialog.mode === 'add' ? 'User created successfully' : 'User updated successfully',
            'success'
          );
          fetchUsers();
          handleDialogClose();
        } else {
          showSnackbar(
            `Failed to ${userDialog.mode === 'add' ? 'create' : 'update'} user`,
            'error'
          );
        }
      })
      .catch(error => {
        console.error(`Error ${userDialog.mode === 'add' ? 'creating' : 'updating'} user:`, error);
        
        // Handle validation errors from the server
        if (error.response && error.response.data && error.response.data.errors) {
          setUserDialog(prev => ({
            ...prev,
            errors: error.response.data.errors
          }));
        } else {
          showSnackbar(
            `Error ${userDialog.mode === 'add' ? 'creating' : 'updating'} user: ${error.response?.data?.message || error.message}`,
            'error'
          );
        }
      });
  };

  // Handle user deletion
  const handleDeleteUser = () => {
    api.delete(`/users/${deleteDialog.userId}`)
      .then(response => {
        if (response.data.success) {
          showSnackbar('User deleted successfully', 'success');
          fetchUsers();
          setDeleteDialog({ open: false, userId: null, userName: '' });
        } else {
          showSnackbar('Failed to delete user', 'error');
        }
      })
      .catch(error => {
        console.error('Error deleting user:', error);
        showSnackbar('Error deleting user: ' + (error.response?.data?.message || error.message), 'error');
      });
  };

  // Show snackbar message
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false
    }));
  };

  // Handle pagination change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
          System Users
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={8}>
            <TextField
              label="Search Users"
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Search by name, email, role, or office..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button 
              variant="outlined" 
              startIcon={<RefreshIcon />}
              onClick={fetchUsers}
            >
              Refresh
            </Button>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={handleAddUser}
              sx={{ 
                bgcolor: themeColors.primary,
                '&:hover': {
                  bgcolor: themeColors.primaryLight
                }
              }}
            >
              Add User
            </Button>
          </Grid>
        </Grid>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Office</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.length > 0 ? (
                    filteredUsers
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>{user.name}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>{user.role}</TableCell>
                          <TableCell>{user.office}</TableCell>
                          <TableCell>
                            <Chip 
                              label={user.status} 
                              size="small" 
                              color={user.status === 'Active' ? 'success' : 'error'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="View Details">
                              <IconButton size="small" onClick={() => handleViewUser(user)}>
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit User">
                              <IconButton size="small" onClick={() => handleEditUser(user)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete User">
                              <IconButton size="small" onClick={() => handleDeleteConfirm(user)}>
                                <DeleteIcon fontSize="small" color="error" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        {searchTerm ? 'No users found matching your search criteria' : 'No users found'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredUsers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>
      
      {/* Add/Edit User Dialog */}
      <Dialog 
        open={userDialog.open} 
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{userDialog.title}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                name="name"
                label="Full Name"
                fullWidth
                value={userDialog.data.name}
                onChange={handleInputChange}
                error={Boolean(userDialog.errors.name)}
                helperText={userDialog.errors.name}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="email"
                label="Email Address"
                type="email"
                fullWidth
                value={userDialog.data.email}
                onChange={handleInputChange}
                error={Boolean(userDialog.errors.email)}
                helperText={userDialog.errors.email}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Chip label={userDialog.mode === 'edit' ? 'Change Password (Optional)' : 'Password'} size="small" />
              </Divider>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="password"
                label="Password"
                type="password"
                fullWidth
                value={userDialog.data.password}
                onChange={handleInputChange}
                error={Boolean(userDialog.errors.password)}
                helperText={userDialog.errors.password}
                required={userDialog.mode === 'add'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="password_confirmation"
                label="Confirm Password"
                type="password"
                fullWidth
                value={userDialog.data.password_confirmation}
                onChange={handleInputChange}
                error={Boolean(userDialog.errors.password_confirmation)}
                helperText={userDialog.errors.password_confirmation}
                required={userDialog.mode === 'add'}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Chip label="User Details" size="small" />
              </Divider>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={Boolean(userDialog.errors.role)}>
                <InputLabel>Role</InputLabel>
                <Select
                  name="role"
                  value={userDialog.data.role}
                  label="Role"
                  onChange={handleInputChange}
                >
                  <MenuItem value="Administrator">Administrator</MenuItem>
                  <MenuItem value="Office Manager">Office Manager</MenuItem>
                  <MenuItem value="Staff">Staff</MenuItem>
                </Select>
                {userDialog.errors.role && (
                  <FormHelperText>{userDialog.errors.role}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={Boolean(userDialog.errors.office)}>
                <InputLabel>Office/Department</InputLabel>
                <Select
                  name="office"
                  value={userDialog.data.office}
                  label="Office/Department"
                  onChange={handleInputChange}
                >
                  <MenuItem value="">Select an Office</MenuItem>
                  {offices.map((office) => (
                    <MenuItem key={office.value} value={office.value}>
                      {office.label}
                    </MenuItem>
                  ))}
                </Select>
                {userDialog.errors.office && (
                  <FormHelperText>{userDialog.errors.office}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth error={Boolean(userDialog.errors.status)}>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={userDialog.data.status}
                  label="Status"
                  onChange={handleInputChange}
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
                {userDialog.errors.status && (
                  <FormHelperText>{userDialog.errors.status}</FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button 
            onClick={handleSubmitUser} 
            variant="contained"
            sx={{ 
              bgcolor: themeColors.primary,
              '&:hover': {
                bgcolor: themeColors.primaryLight
              }
            }}
          >
            {userDialog.mode === 'add' ? 'Create User' : 'Update User'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* View User Dialog */}
      <Dialog
        open={viewDialog.open}
        onClose={() => setViewDialog({ ...viewDialog, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>User Details</DialogTitle>
        <DialogContent>
          {viewDialog.user && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" color="text.secondary">Full Name</Typography>
                <Typography variant="body1">{viewDialog.user.name}</Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Typography variant="subtitle2" color="text.secondary">Email Address</Typography>
                <Typography variant="body1">{viewDialog.user.email}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" color="text.secondary">Role</Typography>
                <Typography variant="body1">{viewDialog.user.role}</Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Typography variant="subtitle2" color="text.secondary">Office/Department</Typography>
                <Typography variant="body1">{viewDialog.user.office}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                <Chip 
                  label={viewDialog.user.status} 
                  size="small" 
                  color={viewDialog.user.status === 'Active' ? 'success' : 'error'}
                />
              </Grid>
              {viewDialog.user.last_login && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary">Last Login</Typography>
                  <Typography variant="body1">
                    {new Date(viewDialog.user.last_login).toLocaleString()}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog({ ...viewDialog, open: false })}>Close</Button>
          {viewDialog.user && (
            <Button 
              onClick={() => {
                setViewDialog({ ...viewDialog, open: false });
                handleEditUser(viewDialog.user);
              }}
              variant="outlined"
              startIcon={<EditIcon />}
            >
              Edit User
            </Button>
          )}
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ ...deleteDialog, open: false })}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the user <strong>{deleteDialog.userName}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ ...deleteDialog, open: false })}>Cancel</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagementTab;